import { env } from '@/config/env';

import { redact } from './redact';

/**
 * The only way this application writes to the console.
 *
 * <h2>Why not `console` directly</h2>
 *
 * Three things a bare `console.log` cannot do, and all three have already cost
 * something here.
 *
 * <p><b>It cannot redact.</b> `LoginPage` logged the whole Google sign-in response,
 * and `response.token` was the credential — an auth token written to the browser
 * console on every social sign-in, readable by any extension. Every argument that
 * passes through here is scrubbed first; see `redact.ts`.
 *
 * <p><b>It cannot be switched off.</b> 134 `console` calls shipped to production,
 * printing the application's internal state to anybody who opens dev tools. `debug`
 * and `info` are dropped outside development; `warn` and `error` always go out,
 * because a warning nobody sees is not a warning.
 *
 * <p><b>It cannot be replaced.</b> The day this reports to Sentry or a log
 * collector, that is a change to this file. Spread across a hundred and thirty call
 * sites, it is a change to a hundred and thirty files, and the one that gets missed
 * is the one that matters.
 *
 * <h2>It must never throw</h2>
 *
 * A logger that fails takes down whatever was reporting a problem, and replaces a
 * readable stack trace with an unrelated one. Every call is wrapped, and the
 * fallback is the raw console — degraded, but never silent and never fatal.
 *
 * <p>That is also why there is no exception for `ErrorBoundary`. It used
 * `console.error` deliberately, on the reasoning that a reporting call which throws
 * during a crash makes the crash unreadable. The reasoning was right; the remedy is
 * a logger that cannot throw, not a rule with a hole in it.
 */

type Level = 'debug' | 'info' | 'warn' | 'error';

/**
 * Whether the quiet levels are printed at all.
 *
 * <p>Read once. A logger that consulted the environment on every call would be doing
 * it a hundred times a second on a busy screen, and nothing about the answer changes
 * while the tab is open.
 */
const VERBOSE = env.isDevelopment;

/**
 * Writes one line, and cannot fail while doing it.
 *
 * <p>The `catch` is not defensive padding. `redact` walks arbitrary objects, and an
 * object with a throwing getter — a proxy, a partially constructed class — would
 * otherwise turn a log statement into the error being logged.
 */
function write(level: Level, message: string, context?: unknown): void {
  try {
    if (context === undefined) {
      console[level](message);
      return;
    }
    console[level](message, redact(context));
  } catch {
    // Degraded, never silent. Whatever went wrong above, the message itself is a
    // plain string the caller supplied and printing it costs nothing.
    try {
      console[level](message);
    } catch {
      // The console itself is unusable. Nothing further to try, and throwing from
      // here would replace the caller's problem with this one.
    }
  }
}

export const logger = {
  /**
   * Development only. For tracing what the application is doing while working on it.
   *
   * <p>Dropped entirely in a build, so it costs a boolean check and is the right
   * thing to reach for rather than a `console.log` somebody means to delete later
   * and does not.
   */
  debug(message: string, context?: unknown): void {
    if (VERBOSE) {
      write('debug', message, context);
    }
  },

  /**
   * Development only. Something ordinary that a person following along would want to
   * know — a session restored, a flow completed.
   */
  info(message: string, context?: unknown): void {
    if (VERBOSE) {
      write('info', message, context);
    }
  },

  /**
   * Always printed. Something is wrong and the application carried on anyway.
   *
   * <p>Not suppressed in production, because the whole value of a warning is that
   * somebody sees it when it happens rather than when they go looking.
   */
  warn(message: string, context?: unknown): void {
    write('warn', message, context);
  },

  /** Always printed. Something failed and the person will notice. */
  error(message: string, context?: unknown): void {
    write('error', message, context);
  },
} as const;
